import { apiRequest } from "./api";
import { isAgentWorkspace, workspaceServiceWorker } from "./registerServiceWorker";

const workspaceHeaders = (staff: boolean) => ({ "x-support-workspace": staff ? "staff" : "customer" });

function decodeBase64Url(value: string) {
  const padding = "=".repeat((4 - (value.length % 4)) % 4);
  const decoded = atob((value + padding).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from(decoded, (character) => character.charCodeAt(0));
}

export function canUsePush() {
  return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

export async function getPushSubscription(staff = isAgentWorkspace()) {
  if (!canUsePush()) return null;
  const scope = new URL(staff ? "/agent/" : "/", location.origin).href;
  const registration = await navigator.serviceWorker.getRegistration(scope);
  if (!registration || registration.scope !== scope) return null;
  return registration.pushManager.getSubscription();
}

export async function subscribeToPush(vapidPublicKey: string) {
  const staff = isAgentWorkspace();
  if (!canUsePush()) throw new Error("Push notifications are not supported by this browser.");
  const permission = await Notification.requestPermission();
  if (permission !== "granted") throw new Error("Notification permission was not granted.");

  const registration = await workspaceServiceWorker(staff);
  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: decodeBase64Url(vapidPublicKey),
    });
  }

  await apiRequest("/push/subscriptions", {
    method: "POST",
    headers: workspaceHeaders(staff),
    body: {
      subscription: subscription.toJSON(),
      userAgent: navigator.userAgent,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
  });
  return subscription;
}

export async function unsubscribeFromPush() {
  const staff = isAgentWorkspace();
  const subscription = await getPushSubscription(staff);
  if (!subscription) return;
  await apiRequest("/push/subscriptions", {
    method: "DELETE",
    headers: workspaceHeaders(staff),
    body: { endpoint: subscription.endpoint },
  });
  await subscription.unsubscribe();
}

export async function hasActivePushSubscription() {
  const staff = isAgentWorkspace();
  const subscription = await getPushSubscription(staff);
  if (!subscription) return false;
  const result = await apiRequest<{ active: boolean }>(`/push/subscriptions/status?endpoint=${encodeURIComponent(subscription.endpoint)}`, { headers: workspaceHeaders(staff) });
  return result.active;
}
